/**
 * GroupChatSettingsModal - Rename group, add members, remove members.
 * Creator: full access. Non-creators: read-only, can leave.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { searchUsers } from '../helpers/searchHelpers';
import { updateGroupName, addGroupParticipant, removeGroupParticipant } from '../helpers/messagesHelpers';
import ModalShell from './ModalShell';
import UserAvatar from '../components/Profile/UserAvatar';
import toast from 'react-hot-toast';

const SEARCH_DEBOUNCE_MS = 300;

function GroupChatSettingsModal({ groupConversation, currentUserId, onClose, onSuccess }) {
  const [name, setName] = useState(groupConversation?.display_name || '');
  const [savingName, setSavingName] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    if (groupConversation) {
      setName(groupConversation.display_name || '');
    }
  }, [groupConversation]);

  const isCreator = groupConversation?.creator_id === currentUserId;
  const participants = groupConversation?.participants || [];
  const participantIds = new Set(participants.map((p) => p.user_id));
  const canRemove = participants.length >= 3;
  const canLeave = participants.length >= 2;

  const doSearch = useCallback(async (q) => {
    if (!q?.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const users = await searchUsers(q.trim());
      setResults(Array.isArray(users) ? users : []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  const handleDone = async () => {
    if (!groupConversation) {
      onClose?.();
      return;
    }
    const trimmed = name.trim();
    const original = (groupConversation.display_name || '').trim();
    const nameChanged = trimmed !== original;
    if (nameChanged && isCreator) {
      setSavingName(true);
      try {
        const updated = await updateGroupName(groupConversation.group_conversation_id, trimmed || null);
        toast.success('Group name updated');
        onSuccess?.(updated);
      } catch (err) {
        toast.error(err.message);
        setSavingName(false);
        return;
      }
      setSavingName(false);
    }
    onClose?.();
  };

  const handleRemove = async (user) => {
    if (!isCreator || removing || !groupConversation || !canRemove) return;
    setRemoving(user.user_id);
    try {
      const updated = await removeGroupParticipant(groupConversation.group_conversation_id, user.user_id);
      toast.success(`Removed ${user.displayname || user.username}`);
      onSuccess?.(updated);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRemoving(null);
    }
  };

  const handleLeave = async () => {
    if (!groupConversation || !canLeave || leaving) return;
    setLeaving(true);
    try {
      const result = await removeGroupParticipant(groupConversation.group_conversation_id, currentUserId);
      toast.success('Left group');
      if (result?.left) {
        onSuccess?.(null);
      } else {
        onSuccess?.(result);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLeaving(false);
    }
  };

  const handleSelectUser = (user) => {
    if (user.user_id === currentUserId) return;
    if (participantIds.has(user.user_id)) {
      toast.error('User is already in the chat');
      return;
    }
    setPendingUser(user);
    setShowConfirm(true);
  };

  const handleConfirmAdd = () => {
    if (pendingUser) {
      handleAddUser(pendingUser);
      setShowConfirm(false);
      setPendingUser(null);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingUser(null);
  };

  const handleAddUser = async (user) => {
    if (adding || !groupConversation) return;
    setAdding(user.user_id);
    try {
      const updated = await addGroupParticipant(groupConversation.group_conversation_id, user.user_id);
      toast.success(`Added ${user.displayname || user.username}`);
      onSuccess?.(updated);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(null);
    }
  };

  if (!groupConversation) return null;

  if (showConfirm && pendingUser && isCreator) {
    return (
      <ModalShell title="Confirm add to group" onClose={handleCancelConfirm}>
        <div className="space-y-4">
          <p className="text-ash text-sm">
            New users will be able to see all messages in this chat. Add {pendingUser.displayname || pendingUser.username}?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancelConfirm}
              className="flex-1 py-3 rounded-xl border border-shadow text-ash font-medium hover:bg-shadow"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmAdd}
              disabled={adding !== null}
              className="flex-1 py-3 rounded-xl bg-ether text-void font-medium disabled:opacity-50"
            >
              {adding === pendingUser.user_id ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell title="Group settings" onClose={onClose}>
      <div className="space-y-6">
        <div>
          <label htmlFor="group-name" className="block text-ash text-sm font-medium mb-1">
            Group name
          </label>
          <input
            id="group-name"
            type="text"
            value={name}
            onChange={(e) => isCreator && setName(e.target.value)}
            placeholder="Enter group name..."
            maxLength={100}
            readOnly={!isCreator}
            className={`w-full px-4 py-3 rounded-xl border text-ash placeholder-silver focus:outline-none focus:ring-2 focus:ring-ether/50 ${
              isCreator ? 'bg-shadow border-charcoal' : 'bg-charcoal border-shadow cursor-default'
            }`}
          />
        </div>

        {isCreator && (
        <div>
          <h3 className="text-ash text-sm font-medium mb-2">Add members</h3>
          <input
            id="add-members-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or username..."
            className="w-full px-4 py-3 rounded-xl bg-shadow border border-charcoal text-ash placeholder-silver focus:outline-none focus:ring-2 focus:ring-ether/50 mb-2"
          />
          <div className="max-h-32 overflow-y-auto">
            {searching ? (
              <p className="text-silver text-sm py-2">Searching...</p>
            ) : results.length === 0 ? (
              <p className="text-silver text-sm py-2">
                {query.trim() ? 'No users found' : 'Type to search users'}
              </p>
            ) : (
              <ul className="space-y-1">
                {results.map((u) => {
                  const isParticipant = participantIds.has(u.user_id);
                  const isSelf = u.user_id === currentUserId;
                  const disabled = isParticipant || isSelf || adding !== null;
                  return (
                    <li key={u.user_id}>
                      <button
                        type="button"
                        onClick={() => handleSelectUser(u)}
                        disabled={disabled}
                        className="w-full flex items-center gap-3 p-2 rounded-lg text-left hover:bg-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <UserAvatar user={u} className="w-8 h-8 rounded-full" emojiClassName="text-sm" />
                        <span className="text-ash text-sm flex-1 truncate">
                          {u.displayname || u.username}
                          {isParticipant && ' (in chat)'}
                          {isSelf && ' (you)'}
                        </span>
                        {!isParticipant && !isSelf && (
                          <span className="text-ether text-xs font-medium">
                            {adding === u.user_id ? 'Adding...' : 'Add'}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        )}

        <div>
          <h3 className="text-ash text-sm font-medium mb-2">Participants</h3>
          <ul className="space-y-1">
            {participants.map((p) => {
              const isSelf = p.user_id === currentUserId;
              const showRemove = !isSelf && canRemove;
              return (
                <li
                  key={p.user_id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-shadow"
                >
                  <UserAvatar user={p} className="w-8 h-8 rounded-full" emojiClassName="text-sm" />
                  <span className="text-ash text-sm flex-1 truncate">
                    {p.displayname || p.username}
                    {isSelf && ' (you)'}
                  </span>
                  {showRemove && isCreator && (
                    <button
                      type="button"
                      onClick={() => handleRemove(p)}
                      disabled={removing !== null}
                      className="text-energy text-xs font-medium hover:underline disabled:opacity-50"
                    >
                      {removing === p.user_id ? 'Removing...' : 'Remove'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {!canRemove && participants.length === 2 && (
            <p className="text-silver text-xs mt-2">
              Add more people before removing anyone. Groups need at least 2 members.
            </p>
          )}
        </div>

        <div className="flex gap-2">
          {canLeave && (
            <button
              type="button"
              onClick={handleLeave}
              disabled={leaving}
              className="flex-1 py-3 rounded-xl border border-energy/50 text-energy font-medium hover:bg-energy/10 disabled:opacity-50"
            >
              {leaving ? 'Leaving...' : 'Leave group'}
            </button>
          )}
          <button
            type="button"
            onClick={handleDone}
            disabled={savingName}
            className={`py-3 rounded-xl bg-ether text-void font-medium hover:opacity-90 disabled:opacity-50 ${canLeave ? 'flex-1' : 'w-full'}`}
          >
            {savingName ? 'Saving...' : 'Done'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

export default GroupChatSettingsModal;
